import React, { useEffect, useRef, useState } from "react";
import Layout from "../../components/layout/layout";
import Button from "../../components/library/button";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import PlatVideoCover from "../../assets/images/platform-vid-cover.png";
import { ExploreIcon } from "../../assets/images/svgExports";
import {
  cards,
  features,
  stats,
  whatYouLearn,
} from "../../utils/dataHelpers/platformPageData";

const PlatformPage: React.FC = () => {
  const pageRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const onPlayClick = async () => {
    if (!videoRef.current) return;

    try {
      await videoRef.current.play();
      setIsPlaying(true);
    } catch {
      // Autoplay restrictions / user gesture issues: do nothing.
    }
  };

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: reduce)", () => {
      ScrollTrigger.getAll().forEach((t) => t.kill());
      gsap.globalTimeline.clear();
    });

    mm.add("(prefers-reduced-motion: no-preference)", () => {
      const ctx = gsap.context(() => {
        // HERO (on load)
        gsap
          .timeline({ defaults: { ease: "power3.out", duration: 0.8 } })
          .from(".loyalty-hero-heading", { y: 22, opacity: 0 })
          .from(".loyalty-hero-heading p", { y: 16, opacity: 0 }, "-=0.45")
          .from(
            ".loyalty-hero-heading a",
            { y: 10, opacity: 0, stagger: 0.08 },
            "-=0.45"
          );

        // Generic section reveal for anything marked data-anim
        const sections = gsap.utils.toArray<HTMLElement>("[data-anim]");
        sections.forEach((el) => {
          gsap.fromTo(
            el,
            { y: 28, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.9,
              ease: "power3.out",
              scrollTrigger: {
                trigger: el,
                start: "top 80%",
                toggleActions: "play none none reverse",
              },
            }
          );
        });

        // Nice list stagger for any UL/OL marked data-stagger="list"
        const lists = gsap.utils.toArray<HTMLElement>('[data-stagger="list"]');
        lists.forEach((listEl) => {
          const items = Array.from(listEl.querySelectorAll("li"));
          if (!items.length) return;

          gsap.set(items, { willChange: "transform, opacity" });

          gsap.from(items, {
            opacity: 0,
            y: 18,
            scale: 0.985,
            duration: 0.65,
            ease: "power2.out",
            stagger: { each: 0.08, from: "start" },
            scrollTrigger: {
              trigger: listEl,
              start: "top 82%",
              toggleActions: "play none none reverse",
            },
            clearProps: "transform",
          });
        });

        // Cards batch
        const cardContainers = gsap.utils.toArray<HTMLElement>(
          '[data-stagger="cards"]'
        );
        cardContainers.forEach((wrap) => {
          const items = Array.from(wrap.children) as HTMLElement[];
          if (!items.length) return;

          ScrollTrigger.batch(items, {
            start: "top 85%",
            onEnter: (batch) =>
              gsap.fromTo(
                batch,
                { opacity: 0, y: 22, scale: 0.98 },
                {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  duration: 0.7,
                  ease: "power3.out",
                  stagger: 0.08,
                  overwrite: true,
                }
              ),
            onLeaveBack: (batch) =>
              gsap.to(batch, {
                opacity: 0,
                y: 22,
                scale: 0.98,
                duration: 0.35,
                ease: "power2.out",
                overwrite: true,
              }),
          });
        });

        // Stats batch
        const statContainers = gsap.utils.toArray<HTMLElement>(
          '[data-stagger="stats"]'
        );
        statContainers.forEach((wrap) => {
          const items = Array.from(wrap.children) as HTMLElement[];
          if (!items.length) return;

          ScrollTrigger.batch(items, {
            start: "top 85%",
            onEnter: (batch) =>
              gsap.fromTo(
                batch,
                { opacity: 0, y: 18 },
                {
                  opacity: 1,
                  y: 0,
                  duration: 0.7,
                  ease: "power3.out",
                  stagger: 0.1,
                  overwrite: true,
                }
              ),
            onLeaveBack: (batch) =>
              gsap.to(batch, {
                opacity: 0,
                y: 18,
                duration: 0.3,
                ease: "power2.out",
                overwrite: true,
              }),
          });
        });
      }, pageRef);

      return () => ctx.revert();
    });

    return () => mm.revert();
  }, []);

  return (
    <Layout
      footerData={{
        headerText: "Ready to Transform Customer Loyalty?",
        bodyText:
          "Join hundreds of leading African brands using Bridgetune to drive engagement, retention, and growth. Let's build your success story together.",
      }}
    >
      <div ref={pageRef} className="bg-white mb-48">
        {/* HERO + MISSION */}
        <section
          className="
            relative bg-[#2C73DB] mb-20
            rounded-bl-[80px] rounded-br-[80px]
            md:rounded-bl-[160px] md:rounded-br-[160px]
            lg:rounded-bl-[200px] lg:rounded-br-[200px]
            text-white pt-32 flex items-end justify-center
          "
        >
          <div className="max-w-5xl relative px-4 sm:px-6">
            <div className="loyalty-hero-heading text-center mb-[5%]">
              <div className="inline-flex items-center bg-[#ffffff33] text-xs font-[300] py-2 px-3 rounded-full mb-3 border border-[#fff] shadow-[0_0_18px_rgba(59,130,246,0.25)]">
                Our Platform
              </div>

              <h1 className="text-3xl lg:text-4xl font-medium leading-tight mb-4 px-2">
                Turn Customers Into Loyal Fans
              </h1>
              <p className="text-sm md:text-base text-blue-100 max-w-2xl mx-auto leading-relaxed">
                Everything you need to engage, reward, and retain customers
                across Africa. Powered by technology that's simple to use but
                incredibly powerful.
              </p>

              <Button className="inline-flex mt-8 items-center gap-2 w-3/4 md:w-auto bg-white text-[#2C73DB] hover:bg-blue-50 px-6 text-sm">
                Get Started
                <ExploreIcon />
              </Button>
            </div>
          </div>
        </section>

        {/* MAIN CONTENT */}
        <main className="space-y-20">
          <section
            data-anim="section"
            className="max-w-full md:max-w-6xl mx-auto px-4 sm:px-6 lg:px-8"
          >
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="text-2xl text-[#1E1E1E] md:text-3xl  font-medium mb-2">
                See Our Platform In Action
              </h2>
              <p className="mt-3 text-sm sm:text-base text-[#8e8e8e]">
                Watch how businesses across Africa use Bridgecore to transform
                customer engagement and drive measurable results.
              </p>
            </div>

            <div className="mt-12 grid gap-10 lg:grid-cols-3 lg:items-start">
              <div className="relative lg:col-span-2">
                <div className="relative overflow-hidden rounded-2xl">
                  <video
                    ref={videoRef}
                    className="h-[320px] w-full object-cover sm:h-[380px] lg:h-[460px]"
                    poster={PlatVideoCover}
                    controls={isPlaying}
                    playsInline
                  >
                    <source src="/videos/platform-demo.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>

                  {!isPlaying && (
                    <button
                      type="button"
                      onClick={onPlayClick}
                      className="absolute inset-0 flex items-center justify-center bg-black/0"
                      aria-label="Play video"
                    >
                      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 shadow-md">
                        <svg
                          viewBox="0 0 24 24"
                          className="h-7 w-7 translate-x-[1px] text-blue-600"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </span>
                    </button>
                  )}
                </div>

                <p className="mt-4 text-center text-xs text-[#8e8e8e]">
                  Watch the 3-minute platform overview to see how easy it is to
                  launch campaigns and track results.
                </p>
              </div>

              {/* Right: Learn list */}
              <div className="lg:pt-2">
                <h3 className="text-md font-[100] text-[#1e1e1e]">
                  What You’ll Learn
                </h3>

                <ul data-stagger="list" className="mt-4 space-y-2">
                  {whatYouLearn.map((item, idx) => (
                    <li
                      key={idx}
                      className="rounded-md border border-[#2C73DB] bg-white px-4 py-3 shadow-[0_10px_25px_rgba(15,23,42,0.06)]"
                    >
                      <div className="flex gap-3">
                        <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md  text-blue-600 ">
                          <svg
                            viewBox="0 0 24 24"
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={3}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M20 6L9 17l-5-5" />
                          </svg>
                        </span>

                        <div>
                          <p className="text-[13px] font-semibold text-[#1e1e1e]">
                            {item.title}
                          </p>
                          <p className="mt-1 text-[12px] leading-5 text-[#8e8e8e]">
                            {item.text}
                          </p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="mt-6 w-full">
                  <Button className="w-full rounded-full bg-[#2C73DB] gap-2">
                    Schedule Your Personal Demo
                    <ExploreIcon />
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* Everything you need */}
          <section
            data-anim="section"
            className="space-y-16 mx-auto max-w-full md:max-w-6xl bg-blue-50 rounded-[60px] py-12 px-6"
          >
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-2xl text-[#1E1E1E] md:text-3xl  font-medium mb-2">
                Everything You Need To Succeed
              </h2>
              <p className="text-[#8e8e8e] text-sm md:text-base max-w-3xl mx-auto text-pretty">
                From Campaign Setup To Performance Tracking, Our Platform
                Provides All The Tools And Insights You Need To Drive Measurable
                Results
              </p>
            </div>

            {features.map((feature, idx) => (
              <section
                data-anim="feature"
                className={`grid items-center gap-6 lg:grid-cols-2 ${
                  idx % 2 !== 0
                    ? "lg:[&>*:first-child]:order-2 lg:[&>*:last-child]:order-1"
                    : ""
                }`}
                key={idx}
              >
                <div className="relative px-2 pt-2 pb-12 rounded-[20px] bg-[#2C73DB]">
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src={feature.image}
                      alt={feature.title}
                      className="h-[280px] w-full object-cover sm:h-[340px] lg:h-[420px]"
                      loading="lazy"
                    />
                  </div>
                </div>

                <div className="p-8 bg-white rounded-[20px] px-4 md:px-8">
                  <div className="flex items-start gap-3">
                    <div className="mt-1">{feature.icon}</div>
                    <div>
                      <h3 className="text-xl sm:text-2xl font-[100] text-[#1e1e1e]">
                        {feature.title}
                      </h3>
                      <p className="mt-2 text-sm sm:text-[15px] leading-6 text-[#8e8e8e]">
                        {feature.subTitle}
                      </p>
                    </div>
                  </div>

                  <ul data-stagger="list" className="mt-6 space-y-4">
                    {feature.subSections.map((section, i) => (
                      <li key={i} className="flex gap-3 text-sm text-[#8e8e8e]">
                        <div>{section.sectionIcon}</div>
                        <span className="text-xs">{section.sectionTitle}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>
            ))}
          </section>

          {/* Why brands choose */}

          <section
            className="bg-white px-4 md:px-16 flex flex-col items-center "
            id="loyalty"
          >
            {/* Header Section */}
            <h2 className="text-3xl md:text-4xl font-[100] text-[#1e1e1e] text-center mb-2">
              Why Leading Brands Choose Bridgetune
            </h2>
            <p className="text-gray-500 mb-12 text-center text-sm md:text-base max-w-xl max-w-3xl leading-relaxed">
              More Than Just Technology—We’re Your Partner In Building Lasting
              Customer Relationships
            </p>

            {/* Cards Container */}
            <div className="flex flex-wrap justify-center gap-6">
              {cards.map((card: any, idx) => (
                <div
                  key={card.title}
                  className={`w-full sm:w-80 md:w-64 lg:w-72 p-8 rounded-xl border-2 shadow-sm transition duration-300 flex flex-col items-center
                      ${
                        idx % 2 !== 0
                          ? "bg-blue-600 border-blue-600 text-white"
                          : "bg-white border-blue-200 text-gray-800 hover:shadow-lg"
                      }`}
                >
                  <div className="mb-6">{card.icon}</div>

                  <h3
                    className={`text-xl font-normal mb-3 ${
                      idx % 2 !== 0 ? "text-white" : "text-gray-800"
                    }`}
                  >
                    {card.title}
                  </h3>

                  <p
                    className={`text-center text-md  ${
                      idx % 2 !== 0 ? "text-blue-100" : "text-[#8e8e8e]"
                    }`}
                  >
                    {card.body}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Real results */}
          <section
            data-anim="stats"
            className="max-w-full md:max-w-6xl mx-auto bg-blue-50 px-6 rounded-[40px] md:rounded-[60px] py-12"
          >
            <div className="text-center">
              <h2 className="text-2xl text-[#1E1E1E] md:text-3xl  font-medium mb-2">
                Real Results From Real Brands
              </h2>
              <p className="mt-3 text-sm sm:text-base text-[#8e8e8e]">
                See What Our Platform Can Help You Achieve
              </p>
            </div>

            <div
              data-stagger="stats"
              className="mt-2 flex flex-col  gap-4 sm:flex-row sm:justify-center sm:gap-6"
            >
              {stats.map((stat, idx) => (
                <div
                  key={idx}
                  className="w-full md:max-w-[250px] rounded-3xl bg-blue-600 px-6 py-8 md:py-16 text-center"
                >
                  <h2 className="text-2xl sm:text-[28px] font-extrabold tracking-tight text-white">
                    {stat.value}
                  </h2>
                  <h3 className="mt-2 text-md whitespace-nowrap font-semibold text-white/95">
                    {stat.title}
                  </h3>
                  <span className="mt-1 text-xs text-white">{stat.note}</span>
                </div>
              ))}
            </div>
          </section>
        </main>
      </div>
    </Layout>
  );
};

export default PlatformPage;
